import { ManageAccountsOutlined } from "@mui/icons-material";
import PlanBadge from "@/components/plan/PlanBadge";
import { useOrganization } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Account route topbar identity (GFG-237 item 1). Replaces the generic page-title h1 and the
 * sibling <PlanBadge /> on /account with the caller's own organization name, an icon, and a
 * role-appropriate pill — GFG staff have no plan/tier, so they get PlanBadge's own pill with its
 * label overridden to "Admin Account" instead of the entitlement-derived label, which would
 * otherwise self-hide for them.
 *
 * Prop-free and self-contained, mirroring PlanBadge: reads its own hooks so Drawer.tsx's
 * edit stays a single conditional. Owns the whole title slot for /account and returns null
 * while the org query is in flight, so nothing flashes a half-built header or the literal
 * string "Account" before swapping to the real name.
 */
export default function AccountTopbarIdentity() {
  const { organizationId, isGfg } = usePermissions();
  const { data: org } = useOrganization(organizationId);

  if (!org) return null;

  return (
    <>
      {/* ml-3 lives here now: the icon is the leading element of this header, so it carries
          the 12px inset that lines this header's left edge up with every other page's h1 —
          the parent Box's gap: 1.5 (12px) already spaces icon → name → badge, so the name
          below stays margin-free to avoid doubling that gap. */}
      <ManageAccountsOutlined
        aria-hidden
        className="ml-3"
        sx={{ fontSize: 26, color: "var(--color-dark-dust)" }}
      />
      {/* min-w-0 + truncate so a long name yields space to the pill at 375px instead of
          wrapping and pushing the toolbar out of its fixed height — same recipe as every
          other page title. */}
      <h1 className="text-3xl font-medium min-w-0 truncate">{org.name}</h1>
      {isGfg ? <PlanBadge overrideLabel="Admin Account" /> : <PlanBadge />}
    </>
  );
}
